import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Research Focus", href: "#research" },
  { label: "Team", href: "#team" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (href) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#050505]/85 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#home"
          onClick={handleClick("#home")}
          data-testid="nav-brand"
          className="flex items-center gap-3 group"
        >
          <span className="w-2.5 h-2.5 bg-white block" aria-hidden />
          <span className="font-heading text-[17px] md:text-lg tracking-tight font-medium">
            Noeton Labs
          </span>
          <span className="hidden md:inline font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase ml-2">
            / R&amp;D
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={handleClick(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm tracking-tight text-zinc-300 hover:text-white transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:block">
          <a
            href="#contact"
            onClick={handleClick("#contact")}
            data-testid="nav-cta-contact"
            className="font-mono text-[11px] uppercase tracking-[0.22em] text-black bg-white hover:bg-zinc-200 transition-colors px-5 py-3 inline-flex items-center"
          >
            Get in touch
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white/80 hover:text-white p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-[#050505]/98 backdrop-blur-xl border-t border-white/10"
        >
          <nav className="px-6 py-6 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={handleClick(l.href)}
                className="text-base text-zinc-200 hover:text-white py-2 border-b border-white/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={handleClick("#contact")}
              className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-black bg-white px-5 py-3 inline-flex items-center w-fit"
            >
              Get in touch
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
